import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  fetchDashboardSummary,
  fetchUpcomingConsultations,
  fetchConsultationHistory,
  fetchRecentActivity
} from '../../store/slices/userDashboardSlice';
import { fetchBalance } from '../../store/slices/walletSlice';
import api from '../../services/api';

/* ───────── animations ───────── */
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ───────── layout ───────── */
const Page = styled.div`
  min-height: 100vh;
  background: #FFFDF7;
  padding: 1.5rem;
  @media (max-width: 600px) { padding: 1rem 0.75rem; }
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const WelcomeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  animation: ${slideUp} 0.5s ease-out;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Welcome = styled.h1`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  font-weight: 800;
  margin: 0;
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
`;

const WelcomeSub = styled.p`
  color: #9B8B7A;
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
`;

/* ───────── CTA buttons ───────── */
const CTARow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  animation: ${slideUp} 0.6s ease-out;
  @media (max-width: 600px) { flex-direction: column; }
`;

const CTABtn = styled.button`
  flex: 1;
  padding: 14px 20px;
  border-radius: 14px;
  border: 1px solid ${p => p.color || 'rgba(218,165,32,0.3)'};
  background: linear-gradient(135deg, ${p => p.bg || 'rgba(218,165,32,0.15)'}, transparent);
  color: #2C1810;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${p => p.shadow || 'rgba(218,165,32,0.25)'};
  }
`;

/* ───────── stat cards ───────── */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  animation: ${slideUp} 0.7s ease-out;

  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 380px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(218,165,32,0.1);
  border-radius: 14px;
  padding: 1.2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(139,105,20,0.06);
`;

const StatIcon = styled.div`
  font-size: 1.6rem;
  margin-bottom: 0.4rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${p => p.color || '#2C1810'};
`;

const StatLabel = styled.div`
  color: #9B8B7A;
  font-size: 0.72rem;
  margin-top: 0.2rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/* ───────── section ───────── */
const SectionTitle = styled.h2`
  color: #2C1810;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 2rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.span`
  font-size: 1.2rem;
`;

/* ───────── tabs ───────── */
const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 1rem;
  background: #FFFFFF;
  border-radius: 10px;
  padding: 4px;
  overflow-x: auto;
`;

const TabBtn = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: ${p => p.active ? 'rgba(218,165,32,0.15)' : 'transparent'};
  color: ${p => p.active ? '#2C1810' : '#9B8B7A'};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  &:hover { color: #2C1810; }
`;

/* ───────── history list ───────── */
const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  animation: ${slideUp} 0.5s ease-out;
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFFFFF;
  border: 1px solid rgba(218,165,32,0.08);
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(218,165,32,0.3);
    background: rgba(218,165,32,0.06);
  }

  @media (max-width: 600px) {
    padding: 10px 12px;
    gap: 10px;
  }
`;

const ItemAvatar = styled.div`
  width: 40px;
  height: 50px;
  border-radius: 6px;
  background: linear-gradient(135deg, #DAA520, #B8860B);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  color: #2C1810;
  font-weight: 600;
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  color: #9B8B7A;
  font-size: 0.72rem;
  margin-top: 2px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ItemRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const ItemType = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${p => {
    if (p.type === 'chat') return 'rgba(33,150,243,0.2)';
    if (p.type === 'call') return 'rgba(76,175,80,0.2)';
    if (p.type === 'video') return 'rgba(218,165,32,0.15)';
    return 'rgba(218,165,32,0.1)';
  }};
  color: ${p => {
    if (p.type === 'chat') return '#64b5f6';
    if (p.type === 'call') return '#81c784';
    if (p.type === 'video') return '#DAA520';
    return '#6B5744';
  }};
`;

const ItemDuration = styled.div`
  color: #B8A898;
  font-size: 0.68rem;
  margin-top: 4px;
`;

/* ───────── reviews ───────── */
const ReviewStars = styled.div`
  color: #f9d71c;
  font-size: 0.85rem;
  margin-top: 2px;
`;

/* ───────── empty state ───────── */
const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #B8A898;
  font-size: 0.9rem;
`;

const EmptyIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.4;
`;

/* ───────── wallet ───────── */
const WalletCard = styled.div`
  background: linear-gradient(135deg, rgba(255,152,0,0.15), rgba(255,152,0,0.05));
  border: 1px solid rgba(255,152,0,0.2);
  border-radius: 14px;
  padding: 1.2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  &:hover { border-color: rgba(255,152,0,0.4); }
`;

/* ───────── helpers ───────── */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

const typeIcon = (type) => {
  if (type === 'chat') return '💬';
  if (type === 'call') return '📞';
  if (type === 'video') return '📹';
  return '📋';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ───────── component ───────── */
const UserDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(s => s.auth);
  const { summary, consultationHistory, loading, error } = useSelector(s => s.userDashboard);
  const { balance } = useSelector(s => s.wallet);
  const [historyTab, setHistoryTab] = useState('all');
  const [allConsultations, setAllConsultations] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user?.role === 'astrologer') { navigate('/astrologer/dashboard'); return; }
    if (user?.role === 'admin') { navigate('/admin/invitations'); return; }

    dispatch(fetchDashboardSummary());
    dispatch(fetchUpcomingConsultations({ limit: 5 }));
    dispatch(fetchConsultationHistory({ limit: 20 }));
    dispatch(fetchRecentActivity({ limit: 10 }));
    dispatch(fetchBalance());

    // fetch all consultations for full history
    api.get('/consultations', { params: { limit: 50 } })
      .then(res => {
        const data = res.data?.data?.consultations || res.data?.consultations || res.data || [];
        setAllConsultations(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, [dispatch, isAuthenticated, navigate, user?.role]);

  const stats = summary?.stats || {};

  // Filter consultations by type for tabs
  const getFilteredHistory = () => {
    const list = allConsultations.length > 0 ? allConsultations : consultationHistory;
    if (historyTab === 'all') return list;
    return list.filter(c => c.type === historyTab);
  };

  const chatHistory = allConsultations.filter(c => c.type === 'chat');
  const callHistory = allConsultations.filter(c => c.type === 'call' || c.type === 'video');
  const reviewedConsultations = allConsultations.filter(c => c.rating > 0);
  const filteredHistory = getFilteredHistory();

  return (
    <Page>
      <Content>
        {/* Welcome */}
        <WelcomeRow>
          <div>
            <Welcome>Welcome, {user?.name || 'User'}!</Welcome>
            <WelcomeSub>Your astrology dashboard</WelcomeSub>
          </div>
          <CTABtn
            onClick={() => navigate('/profile/edit')}
            color="rgba(218,165,32,0.15)"
            bg="rgba(218,165,32,0.06)"
            shadow="rgba(218,165,32,0.1)"
            style={{ flex: 'none', padding: '8px 18px', fontSize: '0.82rem' }}
          >
            👤 My Profile
          </CTABtn>
        </WelcomeRow>

        {/* CTA Buttons */}
        <CTARow>
          <CTABtn
            onClick={() => navigate('/talk-to-astrologer')}
            color="rgba(76,175,80,0.4)"
            bg="rgba(76,175,80,0.15)"
            shadow="rgba(76,175,80,0.25)"
          >
            💬 Chat with Astrologer
          </CTABtn>
          <CTABtn
            onClick={() => navigate('/talk-to-astrologer')}
            color="rgba(218,165,32,0.4)"
            bg="rgba(218,165,32,0.15)"
            shadow="rgba(218,165,32,0.25)"
          >
            📞 Call an Astrologer
          </CTABtn>
        </CTARow>

        {/* Stats */}
        <StatsGrid>
          <StatCard>
            <StatIcon>📅</StatIcon>
            <StatValue color="#64b5f6">{stats.upcoming_consultations ?? 0}</StatValue>
            <StatLabel>Upcoming</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>✅</StatIcon>
            <StatValue color="#81c784">{stats.completed_consultations ?? 0}</StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
          <WalletCard onClick={() => navigate('/wallet')}>
            <StatIcon>💰</StatIcon>
            <StatValue color="#ffb74d">₹{balance?.available?.toFixed(0) ?? '0'}</StatValue>
            <StatLabel>Wallet</StatLabel>
          </WalletCard>
          <StatCard>
            <StatIcon>✉️</StatIcon>
            <StatValue color="#90caf9">{stats.unread_messages ?? 0}</StatValue>
            <StatLabel>Unread</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Consultation History with tabs */}
        <SectionTitle><SectionIcon>📋</SectionIcon> Consultation History</SectionTitle>
        <TabRow>
          <TabBtn active={historyTab === 'all'} onClick={() => setHistoryTab('all')}>
            All ({allConsultations.length})
          </TabBtn>
          <TabBtn active={historyTab === 'chat'} onClick={() => setHistoryTab('chat')}>
            💬 Chats ({chatHistory.length})
          </TabBtn>
          <TabBtn active={historyTab === 'call'} onClick={() => setHistoryTab('call')}>
            📞 Calls ({callHistory.length})
          </TabBtn>
        </TabRow>

        <HistoryList>
          {loading.history && filteredHistory.length === 0 ? (
            <EmptyState>Loading...</EmptyState>
          ) : filteredHistory.length === 0 ? (
            <EmptyState>
              <EmptyIcon>{historyTab === 'chat' ? '💬' : historyTab === 'call' ? '📞' : '📋'}</EmptyIcon>
              No {historyTab === 'all' ? '' : historyTab + ' '}consultations yet
            </EmptyState>
          ) : (
            filteredHistory.slice(0, 10).map(c => {
              const astName = c.astrologer?.name || c.astrologer_name || 'Astrologer';
              return (
                <HistoryItem key={c.id} onClick={() => {
                  if (c.status === 'completed' || c.status === 'ongoing') {
                    navigate(`/consultation/${c.id}/chat`);
                  }
                }}>
                  <ItemAvatar>{typeIcon(c.type)}</ItemAvatar>
                  <ItemInfo>
                    <ItemName>{astName}</ItemName>
                    <ItemMeta>
                      <span>{formatDate(c.created_at || c.start_time)}</span>
                      <span>{c.status}</span>
                      {c.duration > 0 && <span>{c.duration} min</span>}
                    </ItemMeta>
                  </ItemInfo>
                  <ItemRight>
                    <ItemType type={c.type}>{c.type || 'session'}</ItemType>
                    {c.amount_paid > 0 && (
                      <ItemDuration>₹{c.amount_paid}</ItemDuration>
                    )}
                    {c.rating > 0 && (
                      <ReviewStars>{'★'.repeat(c.rating)}</ReviewStars>
                    )}
                  </ItemRight>
                </HistoryItem>
              );
            })
          )}
        </HistoryList>

        {/* Reviews Given */}
        <SectionTitle><SectionIcon>⭐</SectionIcon> My Reviews</SectionTitle>
        <HistoryList>
          {reviewedConsultations.length === 0 ? (
            <EmptyState>
              <EmptyIcon>⭐</EmptyIcon>
              No reviews given yet. Rate astrologers after your consultations!
            </EmptyState>
          ) : (
            reviewedConsultations.map(c => {
              const astName = c.astrologer?.name || c.astrologer_name || 'Astrologer';
              return (
                <HistoryItem key={`review-${c.id}`}>
                  <ItemAvatar>{getInitials(astName)}</ItemAvatar>
                  <ItemInfo>
                    <ItemName>{astName}</ItemName>
                    <ItemMeta>
                      <span>{formatDate(c.created_at || c.end_time)}</span>
                      <ItemType type={c.type}>{c.type}</ItemType>
                    </ItemMeta>
                    {c.review && (
                      <div style={{ color: '#6B5744', fontSize: '0.75rem', marginTop: 4, fontStyle: 'italic' }}>
                        "{c.review}"
                      </div>
                    )}
                  </ItemInfo>
                  <ItemRight>
                    <ReviewStars>{'★'.repeat(c.rating)}{'☆'.repeat(5 - c.rating)}</ReviewStars>
                  </ItemRight>
                </HistoryItem>
              );
            })
          )}
        </HistoryList>

        {/* Quick Links */}
        <SectionTitle><SectionIcon>⚡</SectionIcon> Quick Actions</SectionTitle>
        <CTARow>
          <CTABtn
            onClick={() => navigate('/consultation')}
            color="rgba(33,150,243,0.3)"
            bg="rgba(33,150,243,0.1)"
            shadow="rgba(33,150,243,0.2)"
            style={{ fontSize: '0.85rem' }}
          >
            📅 Book Consultation
          </CTABtn>
          <CTABtn
            onClick={() => navigate('/wallet')}
            color="rgba(255,152,0,0.3)"
            bg="rgba(255,152,0,0.1)"
            shadow="rgba(255,152,0,0.2)"
            style={{ fontSize: '0.85rem' }}
          >
            💰 Recharge Wallet
          </CTABtn>
          <CTABtn
            onClick={() => navigate('/my-bookings')}
            color="rgba(218,165,32,0.15)"
            bg="rgba(218,165,32,0.04)"
            shadow="rgba(218,165,32,0.1)"
            style={{ fontSize: '0.85rem' }}
          >
            📒 My Bookings
          </CTABtn>
          <CTABtn
            onClick={() => navigate('/vedic-tools')}
            color="rgba(218,165,32,0.3)"
            bg="rgba(218,165,32,0.08)"
            shadow="rgba(218,165,32,0.2)"
            style={{ fontSize: '0.85rem' }}
          >
            🪷 Vedic Tools
          </CTABtn>
        </CTARow>

      </Content>
    </Page>
  );
};

export default UserDashboard;
